import { ISalesDetails } from "@/@types/Sells/get-details";
import BadgeShared from "@/components/shared/Badge/Badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { cn, userInitials } from "@/lib/utils";
import { motion } from "framer-motion";
import { AlertTriangle, Clock, Crown, Megaphone, User, Users, type LucideIcon } from "lucide-react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import ComissionsCircle from "@/assets/icons/ComissionsCircle";

type SplitRole = "producer" | "coproducer" | "affiliate" | "referral";
type Split = NonNullable<ISalesDetails["splits"]>[number];
type CpaCommission = NonNullable<ISalesDetails["affiliate_cpa_commissions"]>[number];

type RoleVisual = {
  icon: LucideIcon;
  dot: string;
  text: string;
  chip: string;
  avatar: string;
};

type DistributionSegment = {
  key: string;
  label: string;
  amount: number;
  dot: string;
};

const FEES_DOT = "bg-[#52525B]";

const ROLE_VISUALS: Record<SplitRole, RoleVisual> = {
  producer: {
    icon: Crown,
    dot: "bg-primary",
    text: "text-primary",
    chip: "bg-primary/10 text-primary",
    avatar: "bg-primary/15 text-primary",
  },
  coproducer: {
    icon: Users,
    dot: "bg-[#A1A1AA]",
    text: "text-[#A1A1AA]",
    chip: "bg-[#A1A1AA]/10 text-[#A1A1AA]",
    avatar: "bg-[#A1A1AA]/15 text-[#A1A1AA]",
  },
  affiliate: {
    icon: Megaphone,
    dot: "bg-[#EAB308]",
    text: "text-[#EAB308]",
    chip: "bg-[#EAB308]/10 text-[#EAB308]",
    avatar: "bg-[#EAB308]/15 text-[#EAB308]",
  },
  referral: {
    icon: Megaphone,
    dot: "bg-[#EAB308]",
    text: "text-[#EAB308]",
    chip: "bg-[#EAB308]/10 text-[#EAB308]",
    avatar: "bg-[#EAB308]/15 text-[#EAB308]",
  },
};

const getRoleVisual = (role?: string | null): RoleVisual =>
  ROLE_VISUALS[(role as SplitRole) ?? "affiliate"] ?? ROLE_VISUALS.affiliate;

type RoleBadgeProps = {
  role?: string | null;
  label: string;
  isViewer?: boolean;
  iconSize?: number;
  className?: string;
};

function RoleBadge({
  role,
  label,
  isViewer = false,
  iconSize = 11,
  className,
}: RoleBadgeProps) {
  const visual = getRoleVisual(role);
  const Icon = isViewer ? User : visual.icon;

  return (
    <Badge
      variant="outline"
      className={cn(
        "inline-flex shrink-0 items-center gap-1 whitespace-nowrap border-transparent font-semibold",
        isViewer ? "bg-primary/10 text-primary" : visual.chip,
        className,
      )}
    >
      <Icon size={iconSize} className="shrink-0" strokeWidth={2.25} />
      {label}
    </Badge>
  );
}

const stagger = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.08, delayChildren: 0.05 },
  },
};

const fadeInUp = {
  hidden: { opacity: 0, y: 12 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.35, ease: "easeOut" as const },
  },
};

function DistributionBar({ segments }: { segments: DistributionSegment[] }) {
  if (segments.length === 0) return null;

  return (
    <div className="flex h-2 w-full overflow-hidden rounded-full bg-black/30">
      {segments.map((segment) => (
        <div
          key={segment.key}
          className={cn("h-full min-w-0", segment.dot)}
          style={{ flex: segment.amount }}
        />
      ))}
    </div>
  );
}

function SectionLabel({ children }: { children: ReactNode }) {
  return (
    <p className="text-[11px] font-semibold uppercase tracking-widest text-muted">
      {children}
    </p>
  );
}

type ViewerDisplay = {
  role: SplitRole;
  is_producer: boolean;
  is_coproducer: boolean;
  amount: number;
  amount_formatted: string;
  total: number;
  total_formatted: string;
  retention_amount: number;
  retention_amount_formatted: string;
  platform_fee_applied: number;
  platform_fee_applied_formatted: string;
  commission_percentage?: number | null;
  isCpaOnly?: boolean;
};

function findViewerCpaCommission(
  sale: ISalesDetails,
  viewerSplit?: Split,
): CpaCommission | undefined {
  const cpaList = sale.affiliate_cpa_commissions ?? [];
  if (viewerSplit) {
    return cpaList.find(
      (commission) => commission.affiliate.name === viewerSplit.user.name,
    );
  }
  return cpaList.length === 1 ? cpaList[0] : undefined;
}

function mergeCpaIntoViewerDisplay(
  base: ViewerDisplay,
  cpa: CpaCommission,
): ViewerDisplay {
  const cpaAmount = cpa.amount ?? 0;
  if (cpaAmount <= 0 || !cpa.amount_formatted) {
    return base;
  }

  const splitAmount = base.amount ?? 0;
  const splitTotal = base.total ?? 0;
  const isCpaOnly = splitAmount === 0 && splitTotal === 0;

  // Split % non-zero + CPA: keep split on the hero card; CPA stays on the side card.
  if (!isCpaOnly) {
    return base;
  }

  return {
    ...base,
    amount: cpaAmount,
    amount_formatted: cpa.amount_formatted,
    total: cpaAmount,
    total_formatted: cpa.amount_formatted,
    commission_percentage: null,
    isCpaOnly: true,
  };
}

function buildViewerDisplay(sale: ISalesDetails): ViewerDisplay | null {
  const splits = sale.splits ?? [];
  const viewerSplit = splits.find((split) => split.is_viewer);
  const viewerCpaCommission = findViewerCpaCommission(sale, viewerSplit);

  let base: ViewerDisplay | null = null;

  if (sale.viewer_context) {
    base = {
      role: sale.viewer_context.role,
      is_producer: sale.viewer_context.is_producer,
      is_coproducer: sale.viewer_context.is_coproducer,
      amount: sale.viewer_context.amount,
      amount_formatted: sale.viewer_context.amount_formatted,
      total: sale.viewer_context.total,
      total_formatted: sale.viewer_context.total_formatted,
      retention_amount: sale.viewer_context.retention_amount,
      retention_amount_formatted: sale.viewer_context.retention_amount_formatted,
      platform_fee_applied: sale.viewer_context.platform_fee_applied,
      platform_fee_applied_formatted: sale.viewer_context.platform_fee_applied_formatted,
      commission_percentage: sale.viewer_context.commission_percentage,
    };
  } else if (viewerSplit) {
    base = {
      role: viewerSplit.role,
      is_producer: viewerSplit.role === "producer",
      is_coproducer: viewerSplit.role === "coproducer",
      amount: viewerSplit.amount,
      amount_formatted: viewerSplit.amount_formatted,
      total: viewerSplit.total,
      total_formatted: viewerSplit.total_formatted,
      retention_amount: viewerSplit.retention_amount,
      retention_amount_formatted: viewerSplit.retention_amount_formatted,
      platform_fee_applied: viewerSplit.platform_fee_applied,
      platform_fee_applied_formatted: viewerSplit.platform_fee_applied_formatted,
      commission_percentage: viewerSplit.commission_percentage,
    };
  } else if (viewerCpaCommission?.amount_formatted) {
    return {
      role: "affiliate",
      is_producer: false,
      is_coproducer: false,
      amount: viewerCpaCommission.amount,
      amount_formatted: viewerCpaCommission.amount_formatted,
      total: viewerCpaCommission.amount,
      total_formatted: viewerCpaCommission.amount_formatted,
      retention_amount: 0,
      retention_amount_formatted: "",
      platform_fee_applied: 0,
      platform_fee_applied_formatted: "",
      commission_percentage: null,
      isCpaOnly: true,
    };
  }

  if (!base) {
    return null;
  }

  if (viewerCpaCommission) {
    return mergeCpaIntoViewerDisplay(base, viewerCpaCommission);
  }

  return base;
}

interface SalesCommissionsSectionProps {
  sale: ISalesDetails;
}

export default function SalesCommissionsSection({
  sale,
}: SalesCommissionsSectionProps) {
  const { t } = useTranslation(["sales", "common"]);

  const cpaList = sale.affiliate_cpa_commissions ?? [];
  const splits = sale.splits ?? [];
  const viewerSplit = splits.find((split) => split.is_viewer);
  const viewerCpaCommission = findViewerCpaCommission(sale, viewerSplit);

  const resolvedViewer = buildViewerDisplay(sale);
  const isPrivilegedViewer = !!(
    resolvedViewer?.is_producer || resolvedViewer?.is_coproducer
  );
  const viewerVisual = resolvedViewer ? getRoleVisual(resolvedViewer.role) : null;

  const viewerRetention = resolvedViewer?.retention_amount ?? 0;
  const hasViewerRetention = viewerRetention > 0;
  const hasViewerCommissionPct =
    resolvedViewer?.commission_percentage != null &&
    !resolvedViewer?.isCpaOnly;

  const viewerMetricItems = [
    resolvedViewer?.amount_formatted
      ? {
          key: "available",
          label: t("availableNow"),
          value: resolvedViewer.amount_formatted,
          highlight: false,
        }
      : null,
    hasViewerRetention && resolvedViewer?.retention_amount_formatted
      ? {
          key: "retention",
          label: t("inRetentionShort"),
          value: resolvedViewer.retention_amount_formatted,
          highlight: true,
        }
      : null,
  ].filter(Boolean) as { key: string; label: string; value: string; highlight: boolean }[];

  const getRoleLabel = (role?: string | null) => {
    if (role === "producer") return t("producer");
    if (role === "coproducer") return t("coproducer");
    return t("affiliate");
  };

  const recipientSplits = splits.filter((split) => split.role !== "referral");
  const visibleRecipientSplits = recipientSplits.filter((split) => (split.amount ?? 0) > 0);
  const visibleCpaList = cpaList.filter((commission) => (commission.amount ?? 0) > 0);
  const hasParticipants = visibleRecipientSplits.length > 0 || visibleCpaList.length > 0;

  const cpaTotalAmount = visibleCpaList.reduce(
    (sum, commission) => sum + (commission.amount ?? 0),
    0,
  );
  const totalFeeAmount = sale.fee?.total_fee ?? 0;

  const distributionSegments: DistributionSegment[] = [
    ...visibleRecipientSplits.map((split) => ({
      key: split.id,
      label: split.is_viewer ? t("youLabel") : getRoleLabel(split.role),
      amount: split.amount ?? 0,
      dot: getRoleVisual(split.role).dot,
    })),
    ...(cpaTotalAmount > 0
      ? [
          {
            key: "affiliate-cpa",
            label: t("affiliate"),
            amount: cpaTotalAmount,
            dot: ROLE_VISUALS.affiliate.dot,
          },
        ]
      : []),
    ...(totalFeeAmount > 0
      ? [
          {
            key: "fees",
            label: t("feesLegend"),
            amount: totalFeeAmount,
            dot: FEES_DOT,
          },
        ]
      : []),
  ].filter((segment) => segment.amount > 0);

  const feeLines =
    isPrivilegedViewer && (sale.fee?.total_fee ?? 0) > 0
      ? [
          {
            id: "processing-fee",
            label: t("processingFee"),
            value: sale.fee?.total_fee_formatted ?? "",
          },
        ]
      : [];

  const showViewerCpaCard =
    !!viewerCpaCommission &&
    (viewerCpaCommission.amount ?? 0) > 0 &&
    !resolvedViewer?.isCpaOnly &&
    !isPrivilegedViewer;

  const showViewerCommissionCard =
    !!resolvedViewer &&
    !!viewerVisual &&
    ((resolvedViewer.amount ?? 0) > 0 || !!resolvedViewer.isCpaOnly);

  const showDistributionBar = distributionSegments.length > 0;
  const showUnbalancedBadge = sale.splits_summary?.is_balanced === false;
  const showDistributionCard =
    isPrivilegedViewer &&
    (showDistributionBar || hasParticipants || sale.total_formatted);

  const hasCommissionContent =
    showViewerCommissionCard || showDistributionCard || showViewerCpaCard;

  if (!hasCommissionContent) return null;

  const getSplitSubtext = (split: Split) => {
    if (split.commission_percentage == null) return null;
    return t("pctOfNetSplit", { value: split.commission_percentage });
  };

  const renderSplitRow = (split: Split) => {
    if ((split.amount ?? 0) <= 0) return null;

    const canSeeDetails = isPrivilegedViewer || split.is_viewer;
    const visual = getRoleVisual(split.role);
    const subtext = getSplitSubtext(split);
    const amount = canSeeDetails ? split.total_formatted : split.amount_formatted;

    if (!amount) return null;

    return (
      <div
        key={split.id}
        className="p-4 sm:px-5 md:px-6 md:py-4"
      >
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
          <div className="flex min-w-0 items-center gap-3">
            <Avatar className={cn("h-9 w-9 shrink-0", visual.avatar)}>
              <AvatarFallback className={cn(visual.avatar, "text-xs font-semibold")}>
                {userInitials(split.user.name)}
              </AvatarFallback>
            </Avatar>
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-1.5">
                <span className="truncate text-sm font-medium text-colorText">
                  {split.user.name}
                </span>
                {split.is_viewer && (
                  <RoleBadge
                    role={split.role}
                    label={t("youLabel")}
                    isViewer
                    className="px-1.5 py-0 text-[10px]"
                  />
                )}
              </div>
              <div className="mt-1">
                <RoleBadge
                  role={split.role}
                  label={getRoleLabel(split.role)}
                  className="px-1.5 py-0 text-[10px]"
                />
              </div>
              {subtext && (
                <span className="mt-0.5 block text-[11px] text-muted">{subtext}</span>
              )}
            </div>
          </div>

          <span className="shrink-0 text-sm font-bold text-colorText sm:text-right">
            {amount}
          </span>
        </div>
      </div>
    );
  };

  const renderCpaRow = (commission: CpaCommission) => {
    if ((commission.amount ?? 0) <= 0) return null;

    const visual = getRoleVisual("affiliate");

    return (
      <div
        key={commission.id}
        className="p-4 sm:px-5 md:px-6 md:py-4"
      >
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
          <div className="flex min-w-0 items-center gap-3">
            <Avatar className={cn("h-9 w-9 shrink-0", visual.avatar)}>
              <AvatarFallback className={cn(visual.avatar, "text-xs font-semibold")}>
                {userInitials(commission.affiliate.name)}
              </AvatarFallback>
            </Avatar>
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-1.5">
                <span className="truncate text-sm font-medium text-colorText">
                  {commission.affiliate.name}
                </span>
                <RoleBadge
                  role="affiliate"
                  label={`${t("affiliate")} · CPA`}
                  className="px-1.5 py-0 text-[10px]"
                />
                {commission.status && commission.status !== "paid" && (
                  <BadgeShared status={commission.status} />
                )}
              </div>
              <span className="mt-0.5 block text-[11px] text-muted">
                {t("fixedValuePerSale")}
              </span>
            </div>
          </div>

          {commission.amount_formatted && (
            <span className={cn("shrink-0 text-sm font-bold sm:text-right", visual.text)}>
              {commission.amount_formatted}
            </span>
          )}
        </div>
      </div>
    );
  };

  return (
    <motion.div
      className="space-y-4 md:space-y-6"
      variants={stagger}
      initial="hidden"
      animate="visible"
    >
      <motion.div variants={fadeInUp} className="flex items-center gap-1">
        <ComissionsCircle />
        <h2 className="text-base font-semibold text-colorText sm:text-lg">
          {t("salesCommissionsTitle")}
        </h2>
      </motion.div>

      {showViewerCommissionCard && resolvedViewer && viewerVisual && (
        <motion.div variants={fadeInUp}>
          <Card className="relative overflow-hidden rounded-2xl border-border p-4 sm:p-5 md:p-6">
            <div className={cn("absolute inset-y-0 left-0 w-1", viewerVisual.dot)} />

            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-3">
              <span className="text-xs font-semibold uppercase tracking-wide text-muted">
                {resolvedViewer.isCpaOnly ? t("affiliateCpa") : t("yourCommission")}
              </span>
              <RoleBadge
                role={resolvedViewer.role}
                label={
                  resolvedViewer.isCpaOnly
                    ? `${t("affiliate")} · CPA`
                    : hasViewerCommissionPct
                      ? `${getRoleLabel(resolvedViewer.role)} · ${resolvedViewer.commission_percentage}%`
                      : getRoleLabel(resolvedViewer.role)
                }
                iconSize={12}
                className="w-fit px-2 py-0.5 text-xs"
              />
            </div>

            <div className="mt-2">
              <span className="text-2xl font-semibold tracking-tight text-colorText sm:text-3xl md:text-4xl">
                {resolvedViewer.total_formatted}
              </span>
              {resolvedViewer.isCpaOnly && (
                <p className="mt-1 text-[11px] font-medium text-muted">
                  {t("fixedValuePerSale")}
                </p>
              )}
            </div>

            {feeLines.length > 0 && (
              <div className="mt-4 space-y-2 text-sm">
                {feeLines.map((line) => (
                  <div
                    key={line.id}
                    className="flex flex-col gap-0.5 sm:flex-row sm:items-center sm:justify-between"
                  >
                    <span className="text-[11px] font-medium text-muted">{line.label}</span>
                    <span className="text-sm font-bold text-colorText">{line.value}</span>
                  </div>
                ))}
              </div>
            )}

            {viewerMetricItems.length > 0 && (
              <div
                className={cn(
                  "mt-5 grid gap-3",
                  viewerMetricItems.length > 1 ? "grid-cols-1 sm:grid-cols-2" : "grid-cols-1",
                )}
              >
                {viewerMetricItems.map((metric) => (
                  <div
                    key={metric.key}
                    className="rounded-xl border border-border bg-black/20 p-3"
                  >
                    <p className="text-[11px] font-medium text-muted">{metric.label}</p>
                    <span
                      className={cn(
                        "mt-0.5 block text-sm font-bold",
                        metric.highlight ? "text-warningStrong" : "text-colorText",
                      )}
                    >
                      {metric.value}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </motion.div>
      )}

      {showViewerCpaCard && viewerCpaCommission && (
        <motion.div variants={fadeInUp}>
          <Card className="overflow-hidden border-border p-0">
            <div className="border-b border-border px-4 pb-2 pt-4 sm:px-5 md:px-6">
              <SectionLabel>{t("affiliateCpaSection")}</SectionLabel>
            </div>
            {renderCpaRow(viewerCpaCommission)}
          </Card>
        </motion.div>
      )}

      {showDistributionCard && (
        <motion.div variants={fadeInUp}>
          <Card className="overflow-hidden border-border p-0">
            <div className="flex flex-col gap-2 p-4 sm:flex-row sm:items-center sm:justify-between sm:gap-3 sm:p-5 md:p-6 md:pb-0">
              <p className="text-sm font-semibold text-colorText">{t("distributionTitle")}</p>
              <div className="flex flex-wrap items-center gap-2">
                {showUnbalancedBadge && (
                  <Badge
                    variant="outline"
                    className="gap-1 border-transparent bg-red-500/10 text-[10px] font-semibold text-red-400"
                  >
                    <AlertTriangle size={11} />
                    {t("unbalanced")}
                  </Badge>
                )}
              </div>
            </div>

            {showDistributionBar && (
              <div className="px-4 pb-4 pt-4 sm:px-5 md:px-6 md:pb-6">
                <DistributionBar segments={distributionSegments} />
                <div className="mt-3 flex flex-wrap gap-x-4 gap-y-2">
                  {distributionSegments.map((segment) => (
                    <div key={segment.key} className="flex items-center gap-1.5 text-xs">
                      <span
                        className={cn("inline-block h-2 w-2 shrink-0 rounded-sm", segment.dot)}
                      />
                      <span className="text-muted">{segment.label}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {hasParticipants && (
              <>
                <div className="border-t border-border px-4 pb-2 pt-4 sm:px-5 md:px-6">
                  <SectionLabel>{t("whoReceives")}</SectionLabel>
                </div>
                <div className="divide-y divide-border">
                  {visibleRecipientSplits.map((split) => renderSplitRow(split))}
                  {visibleCpaList.map((commission) => renderCpaRow(commission))}
                </div>
              </>
            )}

          </Card>
        </motion.div>
      )}
    </motion.div>
  );
}
